import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { BandwidthCountingPill, BandwidthCoverageMatrix, DataList, PageHeader, StatusPill, useBandwidthCoverage, type BandwidthCoverage, type DataListColumn } from "@ozpanel/kit";
import { Section, formatBytes, formatDateTime, When } from "@ozpanel/ui";
import { useAccountsApi, type UsageCurrent } from "../../accountsApi";

// A metric that was never measured is ABSENT, not zero — the collector itself refuses to
// write a failed measurement precisely so "0" never means "we couldn't tell". The UI must
// preserve that distinction: a missing key renders "—", never "0 B".
const metric = (u: UsageCurrent, key: string) =>
  key in u.metrics ? formatBytes(u.metrics[key]) : "—";

const columnsFor = (cov?: BandwidthCoverage): DataListColumn<UsageCurrent>[] => [
  { key: "tenant", label: "Tenant", sortable: true, value: (u) => u.tenant || `#${u.tenant_id}` },
  { key: "disk_bytes", label: "Disk", sortable: true, value: (u) => u.metrics.disk_bytes ?? -1, render: (u) => metric(u, "disk_bytes") },
  { key: "mail_bytes", label: "Mail", sortable: true, value: (u) => u.metrics.mail_bytes ?? -1, render: (u) => metric(u, "mail_bytes") },
  { key: "db_bytes", label: "Databases", sortable: true, value: (u) => u.metrics.db_bytes ?? -1, render: (u) => metric(u, "db_bytes") },
  {
    key: "bandwidth_bytes",
    label: "Bandwidth (mo., in + out)",
    sortable: true,
    value: (u) => u.metrics.bandwidth_bytes ?? -1,
    // Never a bare number: the figure is only whole when every source is measured on every node.
    render: (u) => (
      <span>
        {metric(u, "bandwidth_bytes")}
        {cov && !cov.counting && (
          <span className="ml-2" title={(cov.not_counted ?? []).join("\n")}>
            <StatusPill tone="warn">incomplete</StatusPill>
          </span>
        )}
      </span>
    ),
  },
  {
    key: "measured_at",
    label: "Measured",
    sortable: true,
    value: (u) => u.measured_at ?? "",
    render: (u) =>
      u.stale ? (
        <StatusPill tone="warn">stale · <When value={u.measured_at} /></StatusPill>
      ) : (
        formatDateTime(u.measured_at)
      ),
  },
];

export function UsageScreen() {
  const api = useAccountsApi();
  const usage = useQuery({ queryKey: ["usage-overview"], queryFn: api.usageOverview });
  const coverage = useBandwidthCoverage();
  const navigate = useNavigate();

  return (
    <div>
      <PageHeader
        title="Usage & quotas" subtitle={
          usage.data
            ? `${usage.data.total} accounts, from the last measurement on each node — a dash means not measured, never zero`
            : undefined
        }
      />
      <Section
        title="Bandwidth measurement"
        className="mb-4"
        actions={<BandwidthCountingPill coverage={coverage.data} />}
      >
        {/* The web column IS "are Caddy's HTTP metrics in force on this node" — asked of each
            node's running Caddy every pass, not read from the setting. */}
        <BandwidthCoverageMatrix coverage={coverage.data} />
      </Section>
      <DataList
        items={usage.data?.items ?? []}
        columns={columnsFor(coverage.data)}
        getRowKey={(u) => String(u.tenant_id)}
        onRowClick={(u) => navigate(`/tenants/${u.tenant_id}`)}
        searchable
        searchPlaceholder="Search accounts…" loading={usage.isLoading}
        error={usage.error}
        errorContext="usage" emptyMessage="No usage measured yet." />
    </div>
  );
}
